// ─── The gym's exercise library in the member app ───
// `useGymExercises()` reads `list_org_exercises` for anyone in an active org (cached per
// org, refreshed on foreground at most every 10 minutes; demo links last an hour and a
// failed demo re-reads the list). `GymExerciseMedia` shows instructions and the demo
// (uploaded video, else a YouTube embed or link); `ExerciseDetailSheet` wraps it for the
// coach program card.
import React, { useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { V } from '../utils/theme';
import { Icons } from './Icons';
import { flagOn } from '../utils/org';
import { useOrgFeed } from '../utils/orgFeed';
import { ORG_EXERCISES_CACHE_KEY, gymCatalog, gymById, describeExercise, ytEmbedUrl, ytWatchUrl } from '../utils/orgExercises';

export function useGymExercises() {
  const { enabled, data, load } = useOrgFeed('list_org_exercises', { cacheKey: ORG_EXERCISES_CACHE_KEY, anyRole: true, staleMs: 10 * 60 * 1000 });
  const list = useMemo(() => (enabled && data && Array.isArray(data.exercises) ? data.exercises : []), [enabled, data]);
  const catalog = useMemo(() => gymCatalog(list), [list]);
  const byId = useMemo(() => gymById(list), [list]);
  return { list, catalog, byId, reload: load };
}

// "Gym" tag next to an exercise name
export const GymTag = ({ small }) => (
  <span style={{ fontSize: small ? 9 : 10, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: V.accent,
    background: `${V.accent}14`, border: `1px solid ${V.accent}28`, borderRadius: 4, paddingTop: 1, paddingBottom: 1, paddingLeft: 5, paddingRight: 5, flexShrink: 0 }}>Gym</span>
);

// Instructions + demo for one exercise (`info` from describeExercise). The tip is left to the
// caller when it already shows one.
export function GymExerciseMedia({ info, showTip = false, onDemoError }) {
  // A demo that fails to load re-reads the list once (its signed link likely expired); a
  // file the browser cannot play must not loop, since every read signs a new URL.
  const retried = useRef(false);
  if (!info) return null;
  const demoError = () => { if (!retried.current && onDemoError) { retried.current = true; onDemoError(); } };
  const yt = info.yt && flagOn('youtube_videos') ? info.yt : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-testid="gym-exercise-media">
      {showTip && info.tip && (
        <div style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 12, paddingRight: 12, borderRadius: 10, background: `${V.warn}08`, border: `1px solid ${V.warn}20` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: V.warn, marginBottom: 4 }}>FORM CUE</div>
          <div style={{ fontSize: 12, color: V.text2, lineHeight: 1.5 }}>{info.tip}</div>
        </div>
      )}
      {info.instructions && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: V.text3, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>How to do it</div>
          <div style={{ fontSize: 13, color: V.text2, lineHeight: 1.55, whiteSpace: 'pre-wrap' }} data-testid="gym-exercise-instructions">{info.instructions}</div>
        </div>
      )}
      {info.demo ? (
        <video src={info.demo.url} controls playsInline preload="metadata" onError={demoError} aria-label={`${info.name} demo`} data-testid="gym-exercise-demo"
          style={{ width: '100%', maxHeight: 320, borderRadius: 12, background: '#000', display: 'block' }} />
      ) : yt ? (
        <>
          <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            <iframe src={ytEmbedUrl(yt)} title={`${info.name} video`} loading="lazy" data-testid="gym-exercise-yt"
              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }} />
          </div>
          <a href={ytWatchUrl(yt)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: V.accent, textDecoration: 'none' }}>Open on YouTube</a>
        </>
      ) : null}
    </div>
  );
}

// Modal detail for an exercise id (name, category, tip, instructions, demo).
export function ExerciseDetailSheet({ id, exercises, gym, onClose, onDemoError }) {
  const info = describeExercise(id, { exercises, gym });
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  // Focus the close button on open, Escape closes, focus returns to the opener.
  useEffect(() => {
    const prev = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); try { if (prev && prev.isConnected) prev.focus(); } catch {} };
  }, []);
  if (!info) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9994, display: 'flex', alignItems: 'center', justifyContent: 'center',
      paddingTop: 'max(20px, env(safe-area-inset-top, 0px))', paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))', paddingLeft: 16, paddingRight: 16 }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={info.name} data-testid="exercise-detail" style={{ position: 'relative', background: V.sheetBg, borderRadius: 18,
        paddingTop: 20, paddingBottom: 20, paddingLeft: 20, paddingRight: 20, maxWidth: 420, width: '100%', border: `1px solid ${V.cardBorder}`, boxShadow: V.shadowLg,
        maxHeight: '85vh', overflowY: 'auto', animation: 'popIn .22s cubic-bezier(.2,.8,.3,1) both', fontFamily: V.font, color: V.text }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: V.text }}>{info.name}</div>
              {info.gym && <GymTag />}
            </div>
            {(info.cat || info.equip) && <div style={{ fontSize: 11, color: V.text3, marginTop: 2 }}>{[info.cat, info.equip].filter(Boolean).join(' · ')}</div>}
          </div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, cursor: 'pointer' }}>
            {Icons.x({ size: 18, color: V.text3 })}</button>
        </div>
        <GymExerciseMedia info={info} showTip onDemoError={onDemoError} />
        {!info.tip && !info.instructions && !info.demo && !info.yt && <div style={{ fontSize: 12, color: V.text3 }}>No details for this exercise yet.</div>}
      </div>
    </div>,
    document.body
  );
}
